import React, { useCallback, useEffect, useRef, useState } from "react";
import IconLoader from "@/features/Common/components/IconLoader";
import { Box, Button } from "@mui/material";
import WellnessCategoryCard from "../WellnessCategoryCard";
import { WellnessScoreAndMetricsSection } from "../Common/WellnessScoreAndMetricsSection";
import { useTranslation } from "react-i18next";
import {
  getVitalsMetricsFromHealthProfile,
  getBloodPressureDetailFromProfile,
  getHeartRateDetailFromProfile,
  getBreathingRateDetailFromProfile,
} from "./utils/getVitalsMetricsFromHealthProfile";
import {
  WellnessCategoryCardProps,
  WellnessScoreStatus,
  getWellnessScoreStatusFromScore,
} from "../Common/types";
import { trackEvent } from "@/services/analytics-adapter";
import { nativeVariantKey, navigateToBinahScan } from "@/utils";
import { useMachine } from "@xstate/react";
import {
  vitalsCategoryMachine,
  vitalsCategoryEventNames,
  VitalsCategoryState,
} from "./machine";
import MyBloodPressureScreen from "./BloodPressure";
import MyHeartRateScreen from "./HeartRate";
import MyBreathingRateScreen from "./BreathingRate";
import { MetricItem } from "../Common/types";
import BloodPressureManualEntry from "@/features/HealthScore/pages/BloodPressureManualEntry";
import HeartRateManualEntry from "@/features/HealthScore/pages/HeartRateManualEntry";
import BreathingRateManualEntry from "@/features/HealthScore/pages/BreathingRateManualEntry";
import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import useHealthTrends from "@/features/HealthScore/hooks/useHealthTrends";
import {
  mapHealthTrendsToBloodPressureChartData,
  mapHealthTrendToHeartRateChartData,
  mapHealthTrendToBreathingRateChartData,
} from "@/features/HealthScore/utils/healthTrendsChartData";
import { wellnessCategoryFullScreenOverlaySx } from "../Common/wellnessCategoryFullScreenOverlaySx";

function handleMetricClick(
  metric: MetricItem,
  send: (event: { type: string }) => void
) {
  if (metric.id === "bp") {
    send({ type: vitalsCategoryEventNames.OPEN_BLOOD_PRESSURE });
  } else if (metric.id === "hr") {
    send({ type: vitalsCategoryEventNames.OPEN_HEART_RATE });
  } else if (metric.id === "breathing") {
    send({ type: vitalsCategoryEventNames.OPEN_BREATHING_RATE });
  }
}

export interface VitalsCategoryProps {
  healthProfile?: HealthProfileSuccessResponseData | null;
  refetch?: () => void;
  domainScore?: number;
  domainCompleteness?: number;
  domainLastUpdated?: string;
}

export default function VitalsCategory({
  healthProfile,
  refetch,
  domainScore,
  domainCompleteness,
  domainLastUpdated,
}: VitalsCategoryProps = {}) {
  const { t } = useTranslation();
  const hasScore =
    domainScore != null && domainCompleteness != null && domainCompleteness > 0;
  const [state, send] = useMachine(vitalsCategoryMachine);
  const vitalsSectionRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef(state.value);

  const { data: systolicData, refetch: refetchSystolic } = useHealthTrends(
    "SystolicBloodPressure"
  );
  const { data: diastolicData, refetch: refetchDiastolic } = useHealthTrends(
    "DiastolicBloodPressure"
  );
  const { data: pulseData, refetch: refetchPulse } = useHealthTrends("Pulse");
  const { data: breathingRateData, refetch: refetchBreathing } =
    useHealthTrends("BreathingRate");

  const bloodPressureChartData =
    systolicData &&
    diastolicData &&
    systolicData.length > 0 &&
    diastolicData.length > 0
      ? mapHealthTrendsToBloodPressureChartData(systolicData, diastolicData)
      : [];
  const heartRateChartData =
    pulseData && pulseData.length > 0
      ? mapHealthTrendToHeartRateChartData(pulseData)
      : [];
  const breathingRateChartData =
    breathingRateData && breathingRateData.length > 0
      ? mapHealthTrendToBreathingRateChartData(breathingRateData)
      : [];

  const displayMetrics = getVitalsMetricsFromHealthProfile(
    healthProfile ?? null
  );

  const [listExpanded, setListExpanded] = useState(false);

  useEffect(() => {
    const wasDetailOrFormScreen =
      prevStateRef.current === VitalsCategoryState.BloodPressure ||
      prevStateRef.current === VitalsCategoryState.HeartRate ||
      prevStateRef.current === VitalsCategoryState.BreathingRate ||
      prevStateRef.current === VitalsCategoryState.BloodPressureForm ||
      prevStateRef.current === VitalsCategoryState.HeartRateForm ||
      prevStateRef.current === VitalsCategoryState.BreathingRateForm;
    if (wasDetailOrFormScreen && state.matches(VitalsCategoryState.Dashboard)) {
      setListExpanded(true);
      requestAnimationFrame(() => {
        const el = vitalsSectionRef.current;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    prevStateRef.current = state.value;
  }, [state.value]);

  function startFaceScan() {
    trackEvent({
      event: "action.healthCheckManuallyInitiated",
      source: "Health Score - vitals",
    });
    navigateToBinahScan("/binah-scan?skipScanPrompt=true");
  }

  function handleBack() {
    send({ type: vitalsCategoryEventNames.BACK });
  }

  function handleBackToDetail() {
    send({ type: vitalsCategoryEventNames.BACK_TO_DETAIL });
  }

  const handleBpSave = useCallback(async () => {
    if (refetch) {
      await refetch();
    }
    await Promise.all([refetchSystolic(), refetchDiastolic()]);
    handleBackToDetail();
  }, [refetch, refetchSystolic, refetchDiastolic]);

  const handleHrSave = useCallback(async () => {
    if (refetch) {
      await refetch();
    }
    await refetchPulse();
    handleBackToDetail();
  }, [refetch, refetchPulse]);

  const handleBreathingSave = useCallback(async () => {
    if (refetch) {
      await refetch();
    }
    await refetchBreathing();
    handleBackToDetail();
  }, [refetch, refetchBreathing]);

  const vitalProps: WellnessCategoryCardProps = {
    icon: (
      <IconLoader icon="VitalsIcon" color="primary" sx={{ fontSize: 40 }} />
    ),
    title: "Vitals",
    status:
      domainScore != null || domainCompleteness != null
        ? getWellnessScoreStatusFromScore(domainScore ?? 0, domainCompleteness)
        : undefined,
    description: hasScore
      ? t("Vitals.description.update")
      : t(nativeVariantKey("Vitals.description")),
    lastUpdated: domainLastUpdated,
  };

  if (state.matches(VitalsCategoryState.BloodPressureForm)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <BloodPressureManualEntry
          onBack={handleBackToDetail}
          onCancel={handleBackToDetail}
          onSave={handleBpSave}
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.HeartRateForm)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <HeartRateManualEntry
          onBack={handleBackToDetail}
          onCancel={handleBackToDetail}
          onSave={handleHrSave}
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.BreathingRateForm)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <BreathingRateManualEntry
          onBack={handleBackToDetail}
          onCancel={handleBackToDetail}
          onSave={handleBreathingSave}
        />
      </Box>
    );
  }

  const bpDetail = getBloodPressureDetailFromProfile(healthProfile);
  const hrDetail = getHeartRateDetailFromProfile(healthProfile);
  const breathingDetail = getBreathingRateDetailFromProfile(healthProfile);

  if (state.matches(VitalsCategoryState.BloodPressure)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <MyBloodPressureScreen
          chartData={bloodPressureChartData}
          status={bpDetail?.status ?? WellnessScoreStatus.Healthy}
          lastUpdated={bpDetail?.lastUpdated}
          currentSystolic={bpDetail?.systolic}
          currentDiastolic={bpDetail?.diastolic}
          onBack={handleBack}
          onOpenManualEntry={() =>
            send({ type: vitalsCategoryEventNames.OPEN_BP_FORM })
          }
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.HeartRate)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <MyHeartRateScreen
          chartData={heartRateChartData}
          status={hrDetail?.status ?? WellnessScoreStatus.NeedsAttention}
          lastUpdated={hrDetail?.lastUpdated}
          currentHeartRate={hrDetail?.value}
          onBack={handleBack}
          onOpenManualEntry={() =>
            send({ type: vitalsCategoryEventNames.OPEN_HR_FORM })
          }
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.BreathingRate)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <MyBreathingRateScreen
          metricValue={
            breathingDetail != null ? String(breathingDetail.value) : "14"
          }
          status={breathingDetail?.status ?? WellnessScoreStatus.Healthy}
          lastUpdated={breathingDetail?.lastUpdated}
          chartData={breathingRateChartData}
          onBack={handleBack}
          onOpenManualEntry={() =>
            send({ type: vitalsCategoryEventNames.OPEN_BREATHING_FORM })
          }
        />
      </Box>
    );
  }

  return (
    <Box ref={vitalsSectionRef}>
      <WellnessCategoryCard {...vitalProps}>
        <WellnessScoreAndMetricsSection
          show={hasScore}
          healthScore={domainScore ?? 0}
          metrics={displayMetrics}
          onMetricClick={(metric) => handleMetricClick(metric, send)}
          expanded={listExpanded}
          onExpandedChange={setListExpanded}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={startFaceScan}
          fullWidth
          startIcon={
            hasScore ? null : (
              <IconLoader icon="SmilingFaceInRoundedSquareIcon" />
            )
          }
        >
          {hasScore
            ? t("Vitals.buttonLabel.primary.update")
            : t("Vitals.buttonLabel.primary")}
        </Button>
      </WellnessCategoryCard>
    </Box>
  );
}
